<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { equipIcon, equipTypeList } from '../../item/data'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { getInheritPrice } from '../data'
import { socketEmitAsync } from '@/utils/web-socket'
import { formatNumberWithCommas } from '@/utils'
import type { InheritRes } from '@/types/smithy'
import { getEquipName } from '@/utils/equip'
import { DArrowLeft, QuestionFilled } from '@element-plus/icons-vue'

const userStore = useUserStore()

const equipItemId = ref('')
const equipInfo = computed(() => {
  const res = userStore.itemList.find((item) => item.id === equipItemId.value)
  if (!res) {
    equipItemId.value = ''
    return undefined
  }
  const { name, type, attrs } = equipCollection[res.equipId]
  return { ...res, name, type, attrs }
})
const subEquipItemId = ref('')
const subEquipInfo = computed(() => {
  const res = userStore.itemList.find((item) => item.id === subEquipItemId.value)
  if (!res) {
    subEquipItemId.value = ''
    return undefined
  }
  const { name, type, attrs } = equipCollection[res.equipId]
  return { ...res, name, type, attrs }
})
const equippedList = computed(() =>
  equipTypeList.value.map((item) => {
    const itemId = userStore.equippedList[item]
    const equipInfo = userStore.itemList.find((item) => item.id === itemId)
    if (!equipInfo) return undefined
    const { name, type, attrs } = equipCollection[equipInfo.equipId]
    return { ...equipInfo, name, type, attrs }
  }),
)

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  return items
    .filter((item) => {
      const equippedIdList = Object.values(userStore.equippedList)
      if (equipInfo.value && item.reinforcedLevel <= equipInfo.value.reinforcedLevel) {
        return false
      }
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

function handleChoice(type: 'main' | 'sub' | 'all', item: EquipItemType | undefined) {
  if (item?.supremeAttrs) return ElMessage.warning('至臻装备不可强化')
  if (type === 'all') {
    equipItemId.value = item?.id || ''
    subEquipItemId.value = item?.id || ''
  } else if (type === 'main') equipItemId.value = item?.id || ''
  else subEquipItemId.value = item?.id || ''
}

const inheritResShow = ref(false)
function handleInherit() {
  if (!equipInfo.value || !subEquipInfo.value) return
  const price = ['darkGold', 'myth'].includes(equipInfo.value.quality) ? 0 : getInheritPrice(subEquipInfo.value.reinforcedLevel)
  if (price > userStore.gold) {
    return ElMessage.warning('您的金币不足')
  }
  const { id } = equipInfo.value
  ElMessageBox.confirm('<p class="affix">继承操作会将您选中的装备材料<span class="hp">强化等级重置</span>，是否确认继承？</p>', '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const item = userStore.itemList.find((item) => item.id === id)
      if (!item || !equipInfo.value || !subEquipInfo.value) return

      const mainId = equipInfo.value.id
      const subId = subEquipInfo.value.id
      const { userAttr } = await socketEmitAsync<InheritRes>({ type: 'inheritEquip', data: { mainId, subId } })

      userStore.gold -= price
      userStore.userAttr = userAttr

      const { reinforcedLevel } = subEquipInfo.value
      userStore.itemList.find((item) => item.id === mainId)!.reinforcedLevel = reinforcedLevel
      userStore.itemList.find((item) => item.id === subId)!.reinforcedLevel = 0

      subEquipItemId.value = ''
      inheritResShow.value = true
    })
    .catch(() => {})
}
</script>

<template>
  <div class="inherit">
    <div class="inherit-table border-wrap">
      <h4>锻造台</h4>
      <div class="inherit-equip">
        <CommonBtn size="small" style="width: 100%" @done="handleChoice('all', undefined)">
          <span v-if="equipInfo" v-html="getEquipName(equipInfo, equipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div style="text-align: center">
        <el-icon size="25" style="transform: rotate(90deg)"><DArrowLeft /></el-icon>
      </div>
      <div class="inherit-equip">
        <CommonBtn size="small" style="width: 100%" @done="handleChoice('sub', undefined)">
          <span v-if="subEquipInfo" v-html="getEquipName(subEquipInfo, subEquipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>继承说明</h4>
            <p>1. 继承功能为消耗金币将装备的<span class="info">强化等级</span>继承给其它装备。</p>
            <p>2. 第一次选中的装备为<span class="info">被继承</span>的装备。</p>
            <p>3. 继承操作会将您选中的装备材料<span class="hp">强化等级重置</span>。</p>
            <p>4. 继承花费受强化等级影响，+8及以下时，继承<span class="grow">免费</span>。</p>
            <p>5. 您可以通过点击<span class="info">锻造台</span>中的装备卸下装备。</p>
          </div>
        </el-popover>
      </div>
      <template v-if="equipInfo && subEquipInfo">
        <div class="info-card">
          <p>继承花费：{{ equipInfo.darkGoldAttrs ? 0 : formatNumberWithCommas(getInheritPrice(subEquipInfo.reinforcedLevel)) }}</p>
          <p class="affix" style="justify-content: flex-start">
            继承结果：
            <span
              v-if="subEquipInfo"
              :class="equipInfo.quality"
              v-html="getEquipName({ ...equipInfo, reinforcedLevel: subEquipInfo.reinforcedLevel }, equipInfo.type)"
            ></span>
          </p>
        </div>
        <div class="btn-wrap" v-show="equipInfo && subEquipInfo">
          <el-button type="warning" plain @click="handleInherit">开始继承</el-button>
        </div>
      </template>
    </div>
    <div class="item-list border-wrap" style="flex-shrink: 0; margin-bottom: 10px" v-show="!equipInfo">
      <h4>已装备</h4>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="(item, index) of equippedList" :key="index">
            <CommonBtn
              size="small"
              @done="handleChoice('main', item)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" v-html="getEquipName(item, item.type)"></span>
              <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package">
      <h4>{{ equipInfo ? '选择装备作为材料' : '背包' }}</h4>
      <p class="cur-gold">💰 {{ formatNumberWithCommas(userStore.gold) }}</p>
      <div class="item-btn-wrap btn-wrap item-list-wrap" v-show="itemList.length">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of itemList" :key="item.id">
            <CommonBtn
              size="small"
              @done="handleChoice(!equipInfo ? 'main' : 'sub', item)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
            >
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
      <p class="affix" v-show="equipInfo && !itemList.length" style="padding: 0 5px">您背包中不存在<span class="info">强化等级更高</span>的装备</p>
    </div>

    <el-dialog v-model="inheritResShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">继承成功</span>
      </h2>
      <template v-if="equipInfo">
        <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
          <template #reference>
            <div style="width: 50%; margin: 0 auto">
              <CommonBtn size="small" style="width: 100%">
                <span v-html="getEquipName(equipInfo, equipInfo.type)"></span>
              </CommonBtn>
            </div>
          </template>
          <EquipInfo :equip-info="equipInfo" :show-action="false" style="max-height: 60vh" />
        </el-popover>
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="inheritResShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.inherit {
  display: flex;
  flex-flow: column nowrap;
  overflow: hidden;
  .inherit-table {
    position: relative;
    padding: 0 8px 10px 8px;
    margin-bottom: 10px;
    overflow: auto;
    .inherit-equip {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      text-align: right;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
    }
    .item-btn-wrap {
      overflow: auto;
    }
    &.package {
      flex: 1;
      min-height: 135px;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
  .el-button + .el-button {
    margin-left: 0;
  }
}
</style>
